import { endOfDay, startOfDay } from "date-fns";
import React from "react";

import type { Filter } from "../../../components/FilterChips/FilterChips";
import { formatDateTime } from "../../../components/FormattedDateTime";

import type { ActionItemsFilters, WorkspacesFilters } from "./schemas";

import type { SearchServiceSort } from "../../../api/hooks";

import type {
	ActionItemsSearchSortableFields,
	GetActionItemsSearchParams,
	GetMeetingWorkspacesParams,
	WorkspacesSearchSortableFields,
} from "../../../api/services/defs/internal-api";

import { capitalizeWord } from "../../../utils/stringUtils";

import { actionItemsTableConfig, workspacesTableConfig } from "./config";

/**
 * Convert the filters from the UI into the filter chips schema.
 */
export const convertWorkspaceFiltersToFilterChips = (filters: WorkspacesFilters) => {
	const filterChips: Filter[] = [];
	if (filters.status) {
		filterChips.push({ id: "status", type: "string", label: "Status", value: capitalizeWord(filters.status) });
	}
	if (filters.createdBy) {
		filterChips.push({
			id: "createdBy",
			type: "single-employee",
			label: "Created By",
			value: { id: filters.createdBy.value, name: filters.createdBy.label },
		});
	}
	if (filters.participant) {
		filterChips.push({
			id: "participant",
			type: "single-employee",
			label: "Participant",
			value: { id: filters.participant.value, name: filters.participant.label },
		});
	}
	if (filters.upcomingMeetingStart || filters.upcomingMeetingEnd) {
		const startDate = filters.upcomingMeetingStart
			? formatDateTime(filters.upcomingMeetingStart, { hasTime: false, dateSize: "large" })
			: "";
		const endDate = filters.upcomingMeetingEnd
			? formatDateTime(filters.upcomingMeetingEnd, { hasTime: false, dateSize: "large" })
			: "";
		const label = startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate;
		filterChips.push({
			id: "upcomingMeeting",
			type: "string",
			label: "Upcoming Meeting",
			value: label || "",
		});
	}
	if (filters.previousMeetingStart || filters.previousMeetingEnd) {
		const startDate = filters.previousMeetingStart
			? formatDateTime(filters.previousMeetingStart, { hasTime: false, dateSize: "large" })
			: "";
		const endDate = filters.previousMeetingEnd
			? formatDateTime(filters.previousMeetingEnd, { hasTime: false, dateSize: "large" })
			: "";
		const label = startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate;
		filterChips.push({
			id: "previousMeeting",
			type: "string",
			label: "Previous Meeting",
			value: label || "",
		});
	}

	return filterChips;
};

/**
 * Convert the filters, search string, sort, and page number from the UI into the search service API params.
 *
 * Acting user ID will always be included in the participantIds param.
 */
export const convertWorkspaceFiltersToSearchParams = (
	filters: WorkspacesFilters,
	nameSearch: string,
	sort: SearchServiceSort,
	pageNumber: number,
	actingUserId: string
) => {
	const searchParams: GetMeetingWorkspacesParams = {
		participantIds: [actingUserId],
		limit: workspacesTableConfig.pageSize,
		skip: (pageNumber - 1) * workspacesTableConfig.pageSize,
		sort: { name: sort.name as WorkspacesSearchSortableFields, direction: sort.direction },
	};

	if (filters.status && filters.status !== "any") {
		searchParams.statusEq = filters.status;
	}

	if (filters.createdBy) {
		searchParams.createdById = filters.createdBy.value;
	}

	if (filters.participant) {
		searchParams.participantIds?.push(filters.participant.value);
	}

	// Handle upcoming meeting date range
	if (filters.upcomingMeetingStart) {
		searchParams.upcomingMeetingStartGte = startOfDay(filters.upcomingMeetingStart).toISOString();
	}

	if (filters.upcomingMeetingEnd) {
		// Set to end of day (23:59:59.999) in UTC
		searchParams.upcomingMeetingStartLte = endOfDay(filters.upcomingMeetingEnd).toISOString();
	}

	// Handle previous meeting date range
	if (filters.previousMeetingStart) {
		searchParams.previousMeetingStartGte = startOfDay(filters.previousMeetingStart).toISOString();
	}

	if (filters.previousMeetingEnd) {
		searchParams.previousMeetingStartLte = endOfDay(filters.previousMeetingEnd).toISOString();
	}

	if (nameSearch) {
		searchParams.name = nameSearch;
	}

	return searchParams;
};

/**
 * Convert the filters from the UI into the filter chips schema.
 */
export const convertActionItemFiltersToFilterChips = (filters: ActionItemsFilters, workspaceName?: string) => {
	const filterChips: Filter[] = [];
	if (filters.status) {
		filterChips.push({ id: "status", type: "string", label: "Status", value: capitalizeWord(filters.status) });
	}
	if (filters.createdBy) {
		filterChips.push({
			id: "createdBy",
			type: "single-employee",
			label: "Created By",
			value: { id: filters.createdBy.id, name: filters.createdBy.name },
		});
	}
	if (filters.assignee) {
		filterChips.push({
			id: "assignee",
			type: "single-employee",
			label: "Assignee",
			value: { id: filters.assignee.id, name: filters.assignee.name },
		});
	}
	if (filters.isUnassigned && filters.isUnassigned.length > 0) {
		filterChips.push({
			id: "isUnassigned",
			type: "customRender",
			label: "",
			value: React.createElement("span", null, "Unassigned Items"),
		});
	}
	if (filters.dueDateRange?.from || filters.dueDateRange?.to) {
		const startDate = filters.dueDateRange?.from
			? formatDateTime(
					typeof filters.dueDateRange.from === "string"
						? new Date(filters.dueDateRange.from)
						: filters.dueDateRange.from,
					{ hasTime: false, dateSize: "large" }
				)
			: "";
		const endDate = filters.dueDateRange?.to
			? formatDateTime(
					typeof filters.dueDateRange.to === "string"
						? new Date(filters.dueDateRange.to)
						: filters.dueDateRange.to,
					{ hasTime: false, dateSize: "large" }
				)
			: "";
		const label = startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate;
		filterChips.push({
			id: "dueDateRange",
			type: "string",
			label: "Due Date",
			value: label || "",
		});
	}
	if (filters.workspace && workspaceName) {
		filterChips.push({
			id: "workspace",
			type: "string",
			label: "Meeting Workspace",
			value: workspaceName,
		});
	}

	return filterChips;
};

/**
 * Convert the filters, search string, sort, and page number from the UI into the search service API params.
 */
export const convertActionItemFiltersToSearchParams = (
	filters: ActionItemsFilters,
	nameSearch: string,
	sort: SearchServiceSort,
	pageNumber: number
) => {
	const searchParams: GetActionItemsSearchParams = {
		limit: actionItemsTableConfig.pageSize,
		skip: (pageNumber - 1) * actionItemsTableConfig.pageSize,
		sort: { name: sort.name as ActionItemsSearchSortableFields, direction: sort.direction },
	};

	if (filters.status && filters.status !== "any") {
		searchParams.statusEq = filters.status;
	}

	if (filters.createdBy) {
		searchParams.createdByUserId = filters.createdBy.id;
	}

	if (filters.assignee) {
		searchParams.assigneeUserId = filters.assignee.id;
	}

	if (filters.isUnassigned && filters.isUnassigned.length > 0) {
		searchParams.isUnassigned = "true";
	}

	// Handle due date range
	if (filters.dueDateRange?.from) {
		// Convert string to Date if needed
		const fromDate =
			typeof filters.dueDateRange.from === "string"
				? new Date(filters.dueDateRange.from)
				: filters.dueDateRange.from;

		if (fromDate instanceof Date && !isNaN(fromDate.getTime())) {
			searchParams.dueDateStart = startOfDay(fromDate).toISOString();
		}
	}

	if (filters.dueDateRange?.to) {
		// Convert string to Date if needed
		const toDate =
			typeof filters.dueDateRange.to === "string" ? new Date(filters.dueDateRange.to) : filters.dueDateRange.to;

		if (toDate instanceof Date && !isNaN(toDate.getTime())) {
			searchParams.dueDateEnd = endOfDay(toDate).toISOString();
		}
	}

	if (filters.workspace) {
		searchParams.workspaceId = filters.workspace;
	}

	if (nameSearch) {
		searchParams.name = nameSearch;
	}

	return searchParams;
};
